import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { MouseEvent, useState } from 'react';
import { FormikProps } from 'formik';
import {
    Button,
    Field,
    Input,
    Label,
    makeStyles,
    mergeClasses,
    Spinner,
    Text,
    Textarea,
    tokens,
    Tooltip,
    useId,
} from '@fluentui/react-components';
import {
    DocumentCheckmark20Regular,
    DocumentError20Regular,
    SparkleFilled,
    SparkleRegular,
} from '@fluentui/react-icons';
import { Column, Row } from '../../../common';
import { useUtilities } from '../../../../utilities/useUtilities';

import { useDocumentDownload } from '../../../../data/services/useDocumentDownload';
import { CopilotService } from '../../../../data/services/CopilotService';
import {
    getMimeTypeFromFileName,
    isSupportedDocumentMimeType,
    MAX_DOCUMENT_SIZE,
} from '../../../../data/services/fileAttachments';
import { useCopilotContext } from '../../../copilot/useCopilotContext';
import { useAccess } from '../../../../auth/useAccess';
import { CaseDocument } from '../../../../data/types';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { useValidation } from '../../../../data/types/validation';

const useStyles = makeStyles({
    field: {
        width: '100%',
    },
    errorText: {
        color: tokens.colorStatusDangerForeground1,
    },
});

// Casey's core prompt mandates an opening restatement and tool narration; both are
// suppressed here so the reply can drop straight into the Summary field.
const SUMMARIZE_PROMPT =
    'Summarize the attached document in 1 to 2 paragraphs of plain prose. ' +
    'This is an automated request, so reply with ONLY the summary text. ' +
    'Do not include an opening restatement, headings, bullet points, markdown, preamble, or closing remarks. ' +
    'Do not call any tools. Keep the summary under 3000 characters.';

type DocumentInfoPanelProps = {
    caseDocForm: FormikProps<CaseDocument>;
};

export const DocumentInfoPanel = (props: DocumentInfoPanelProps) => {
    const { caseDocForm } = props;
    const caseDoc = caseDocForm.values as CaseDocument;

    //Hooks
    const utils = useUtilities();
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const { validateCaseDocument } = useValidation();
    const { getDocumentFileAsync } = useDocumentDownload();
    const copilotContext = useCopilotContext();
    const summaryId = useId('document-summary');

    //State
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [summarizeError, setSummarizeError] = useState<string>();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const lifecycleTextClasses = mergeClasses(commonStyles.text.subtleText);

    //Variables
    const dateFormat: Intl.DateTimeFormatOptions = {
        month: 'numeric',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        hour12: true,
        minute: '2-digit',
    };

    //Access
    const canEditDocs = () => {
        return access.cases.canEditDocuments(caseDoc.caseId);
    };
    const canSummarize = () => {
        return !!primaryAccount?.account.features?.copilot && canEditDocs();
    };

    //Logic
    const getSummarizeBlockedReason = (): string | undefined => {
        if (!caseDocForm.values.uploaded) return 'This document has not finished uploading.';
        if (!isSupportedDocumentMimeType(getMimeTypeFromFileName(caseDoc.name || '')))
            return 'Casey can only read PDF, TXT, MD, CSV, JSON, HTML, and XML files.';
        if ((caseDocForm.values.size || 0) > MAX_DOCUMENT_SIZE)
            return 'This document is larger than the 20MB limit.';
        return undefined;
    };
    const summarizeBlockedReason = getSummarizeBlockedReason();

    //UI Events
    const handleSaveClick = (ev: MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation();
        caseDocForm.submitForm();
    };
    const handleCancelClick = (ev: MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation();
        caseDocForm.resetForm();
    };
    const handleSummarizeClick = async (ev: MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation();
        setSummarizeError(undefined);
        setIsSummarizing(true);

        try {
            const file = await getDocumentFileAsync(caseDoc);
            const context = await copilotContext.getContext();
            const summary = (
                await new CopilotService(primaryAccount!).sendSilentMessage(
                    SUMMARIZE_PROMPT,
                    [file],
                    context,
                )
            ).trim();

            if (!summary) {
                setSummarizeError('Casey did not return a summary. Please try again.');
                return;
            }

            caseDocForm.setFieldValue('summary', summary, true);
        } catch (error) {
            setSummarizeError(
                error instanceof Error ? error.message : 'Unable to summarize this document.',
            );
        } finally {
            setIsSummarizing(false);
        }
    };

    return (
        <Column gap='medium' minHeight='fit-content' padding='pb-2'>
            <Label size='large'>Details</Label>
            <Field
                className={styles.field}
                label='Name'
                required
                validationMessage={caseDocForm.errors.name}
            >
                <Input
                    name='name'
                    value={caseDocForm?.values.name || ''}
                    onChange={caseDocForm?.handleChange}
                    placeholder='Enter the Document Name'
                    readOnly={!canEditDocs()}
                    data-testid='document-info-panel-name-input'
                />
            </Field>
            <Row>
                <Column vAlign='middle'>
                    <Label>Size: {utils.formatSize(caseDocForm.values.size!)}</Label>
                </Column>
                <Column vAlign='middle'>
                    <Row>
                        <Column width='auto'>
                            <Label>Uploaded:</Label>
                        </Column>
                        <Column align='left'>
                            {caseDocForm.values.uploaded ? (
                                <DocumentCheckmark20Regular
                                    color={tokens.colorStatusSuccessForeground1}
                                />
                            ) : (
                                <DocumentError20Regular
                                    color={tokens.colorStatusDangerForeground1}
                                />
                            )}
                        </Column>
                    </Row>
                </Column>
            </Row>
            <Field
                className={styles.field}
                label='Description'
                hint={`${caseDocForm.values.description?.length || 0} / ${validateCaseDocument.description.maxLength}`}
                validationMessage={caseDocForm.errors.description}
            >
                <Textarea
                    name='description'
                    value={caseDocForm.values.description || ''}
                    onChange={caseDocForm.handleChange}
                    placeholder='Enter description...'
                    readOnly={!canEditDocs()}
                    data-testid='document-info-panel-description-textarea'
                />
            </Field>
            <Column gap='small'>
                <Row gap='small' noWrap>
                    <Column width='auto' vAlign='middle'>
                        <Label htmlFor={summaryId}>Summary</Label>
                    </Column>
                    {canSummarize() && (
                        <Column flex='1' align='right' padding='pr-1'>
                            <Tooltip
                                relationship='label'
                                content={
                                    summarizeBlockedReason ||
                                    'Ask Casey for a 1 to 2 paragraph summary of this document'
                                }
                            >
                                <Button
                                    appearance='secondary'
                                    size='small'
                                    icon={
                                        isSummarizing ? (
                                            <Spinner size='extra-tiny' />
                                        ) : (
                                            <SparkleFilled color='gold' />
                                        )
                                    }
                                    disabled={isSummarizing}
                                    disabledFocusable={!isSummarizing && !!summarizeBlockedReason}
                                    onClick={handleSummarizeClick}
                                    data-testid='document-info-panel-summarize-btn'
                                />
                            </Tooltip>
                        </Column>
                    )}
                </Row>
                <Field
                    className={styles.field}
                    hint={`${caseDocForm.values.summary?.length || 0} / ${validateCaseDocument.summary.maxLength}`}
                    validationMessage={caseDocForm.errors.summary}
                >
                    <Textarea
                        id={summaryId}
                        name='summary'
                        rows={8}
                        value={caseDocForm.values.summary || ''}
                        onChange={caseDocForm.handleChange}
                        placeholder='Enter summary...'
                        readOnly={!access.cases.canEditDocuments(caseDoc.caseId)}
                        data-testid='document-info-panel-summary-textarea'
                    />
                </Field>
                {!!summarizeError && (
                    <Text className={styles.errorText} size={200}>
                        {summarizeError}
                    </Text>
                )}
            </Column>
            {caseDocForm.values.lifecycle?.createdBy && (
                <Text className={lifecycleTextClasses} italic size={200}>
                    Uploaded:{' '}
                    {utils.date.formatDate(caseDocForm.values.lifecycle?.createdOn, dateFormat)} by{' '}
                    {utils.getUsersName(
                        caseDocForm.values.lifecycle?.createdBy,
                        primaryAccount?.accountUsers || [],
                    )}
                </Text>
            )}
            {caseDocForm.values.lifecycle?.modifiedBy && (
                <Text className={lifecycleTextClasses} italic size={200}>
                    Modified:{' '}
                    {utils.date.formatDate(caseDocForm.values.lifecycle?.modifiedOn, dateFormat)} by{' '}
                    {utils.getUsersName(
                        caseDocForm.values.lifecycle?.modifiedBy,
                        primaryAccount?.accountUsers || [],
                    )}
                </Text>
            )}
            <Row>
                <Column align='right'>
                    <Button
                        appearance='primary'
                        disabled={!caseDocForm.dirty || !caseDocForm.isValid || isSummarizing}
                        onClick={handleSaveClick}
                        data-testid='document-info-panel-save-btn'
                    >
                        Save
                    </Button>
                </Column>
                <Column width='auto'>
                    <Button
                        disabled={!caseDocForm.dirty || isSummarizing}
                        onClick={handleCancelClick}
                        data-testid='document-info-panel-cancel-btn'
                    >
                        Cancel
                    </Button>
                </Column>
            </Row>
        </Column>
    );
};
